/**
 * refresh-coordinator — กันไม่ให้สองแท็บ refresh ด้วย token ใบเดียวกัน (ADR 0228)
 *
 * **ทำไมต้องมี:** refresh token หมุนทิ้งและมี reuse detection ฝั่ง OAuth
 * (`refresh-token.grant.ts` → `revokeFamily`) การทำซ้ำจริงบน dev ชี้ว่า:
 *
 *  - ยิงสองใบด้วย token เดียวกัน **พร้อมกันเป๊ะ** → 200 ทั้งคู่ ไม่มีอะไรพัง
 *  - ยิงใบที่สองด้วย token เก่า **ห่างออกไป 300 ms** → 401 และ **token ใหม่ที่ใบแรก
 *    เพิ่งได้ก็ตายด้วย** เพราะเซิร์ฟเวอร์ถือว่า family ถูกขโมย
 *
 * ตัวที่ฆ่า session จึงเป็น **request ที่ถือ token เก่า** ไม่ใช่ request ที่เกิดพร้อมกัน
 * การ dedupe เฉย ๆ ไม่พอ — ต้องอ่าน token ใหม่ *หลังได้ล็อก* แล้วถ้าเปลี่ยนไปแล้ว
 * ต้องไม่ยิงอะไรเลย
 *
 * `refreshInFlight` ของแต่ละ store (ADR 0052 R9) ยังคงไว้: มันกันภายในแท็บ
 * ตัวนี้กันข้ามแท็บ คนละชั้นกัน
 */

/** ผลของการประสานงาน — `skipped` คือกรณีที่แท็บอื่นทำให้แล้ว */
export type CoordinatedResult = "refreshed" | "skipped" | "failed";

interface CoordinateOptions {
  /** ชื่อล็อก — staff กับ portal ต้องคนละชื่อ ไม่งั้นบล็อกกันเปล่า ๆ */
  lockName: string;
  /** อ่าน refresh token ปัจจุบันจาก storage */
  readToken: () => string | null;
  /** ยิง refresh จริงด้วย token ที่ให้ไป — คืน true เมื่อสำเร็จและเขียนผลแล้ว */
  run: (token: string) => Promise<boolean>;
}

type LockManager = {
  request: (name: string, fn: () => Promise<CoordinatedResult>) => Promise<CoordinatedResult>;
};

function lockManager(): LockManager | null {
  if (typeof navigator === "undefined") return null;
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  return locks && typeof locks.request === "function" ? locks : null;
}

/**
 * รัน `run` โดยรับประกันว่า token ที่ส่งเข้าไปคือตัวล่าสุดที่ storage ถืออยู่
 *
 * ถ้าระหว่างรอคิวมีแท็บอื่น refresh สำเร็จ (token ใน storage เปลี่ยน) จะคืน `"skipped"`
 * โดยไม่ยิงอะไรเลย — ผู้เรียกถือว่าเป็นความสำเร็จ เพราะ storage มี token ที่ใช้ได้แล้ว
 */
export async function coordinatedRefresh({
  lockName,
  readToken,
  run,
}: CoordinateOptions): Promise<CoordinatedResult> {
  const tokenAtEntry = readToken();
  if (!tokenAtEntry) return "failed";

  const critical = async (): Promise<CoordinatedResult> => {
    // อ่านใหม่ *ที่นี่* คือทั้งหมดของ ADR นี้ — ค่าที่อ่านไว้ก่อนขอล็อกอาจเก่าไปแล้ว
    const current = readToken();
    if (!current) return "failed";
    if (current !== tokenAtEntry) return "skipped";
    return (await run(current)) ? "refreshed" : "failed";
  };

  const locks = lockManager();
  if (!locks) {
    /* ไม่มี Web Locks (Safari เก่า / WebView บางตัว) — ตั้งใจ **ไม่** ทำล็อกเลียนแบบด้วย
       localStorage เพราะล็อกที่ปล่อยเองไม่ได้เมื่อแท็บแครชจะทำให้ refresh ค้างถาวร
       ซึ่งแย่กว่าอาการเดิม ทำได้แค่เช็คก่อนยิง — ลดโอกาสชน ไม่เป็นศูนย์ (ADR 0228 D2) */
    return critical();
  }
  return locks.request(lockName, critical);
}
